import { createTheme } from '@mui/material/styles';
import { legalInsightsTheme } from './LegalInsightsTheme';

declare module '@mui/material/styles' {
  interface TypographyVariants {
    listItem: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    listItem?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    listItem: true;
  }
}

const almaStyleArticleTheme = createTheme({
  ...legalInsightsTheme,
  typography: {
    ...legalInsightsTheme.typography,
    listItem: {},
  },
  components: {
    ...legalInsightsTheme.components,
    /* MuiLink needs to be defned here so it can be accessed later to define
    colors with the palette. */
    MuiLink: {},
    MuiListItem: {},
    MuiTableCell: {},
  },
});

// @NOTE: Check the Article.css for styles to be updated if you change anything here
if (almaStyleArticleTheme.components?.MuiLink) {
  almaStyleArticleTheme.components.MuiLink = {
    defaultProps: {
      underline: 'hover',
    },
    styleOverrides: {
      underlineHover: {
        color: almaStyleArticleTheme.palette.text.secondary,
        '&:hover': {
          color: almaStyleArticleTheme.palette.text.primary,
        },
        '&:focus': {
          color: almaStyleArticleTheme.palette.text.primary,
        },
      },
    },
  };
}

almaStyleArticleTheme.typography = {
  ...legalInsightsTheme.typography,
  fontSize: 16,
};

almaStyleArticleTheme.typography.h1 = {
  fontSize: '2rem',
  lineHeight: '133%',
  fontWeight: 900,
  padding: '0 0 2rem',
  [almaStyleArticleTheme.breakpoints.up('sm')]: {
    fontSize: '2.5rem',
    padding: '0 0 1.875rem',
  },
  [almaStyleArticleTheme.breakpoints.up('md')]: {
    fontSize: '2.75rem',
    lineHeight: '150%',
    padding: '0 0 1.75rem',
  },
};

almaStyleArticleTheme.typography.h2 = {
  fontSize: '1.8rem',
  lineHeight: '133%',
  fontWeight: 900,
  padding: '1rem 0',
  [almaStyleArticleTheme.breakpoints.up('sm')]: {
    fontSize: '2rem',
    fontWeight: 800,
    padding: '1.125rem 0',
  },
  [almaStyleArticleTheme.breakpoints.up('md')]: {
    fontSize: '2.25rem',
    lineHeight: '150%',
    fontWeight: 800,
    padding: '1.25rem 0',
  },
};

almaStyleArticleTheme.typography.h3 = {
  fontSize: '1.6rem',
  lineHeight: '133%',
  fontWeight: 800,
  padding: '1rem 0',
  [almaStyleArticleTheme.breakpoints.up('sm')]: {
    fontSize: '1.75rem',
    padding: '1.125rem 0 1rem',
  },
  [almaStyleArticleTheme.breakpoints.up('md')]: {
    fontSize: '2rem',
    lineHeight: '150%',
    padding: '1.25rem 0',
  },
};

almaStyleArticleTheme.typography.h4 = {
  fontSize: '1.425rem',
  lineHeight: '133%',
  fontWeight: 800,
  padding: '1rem 0',
  [almaStyleArticleTheme.breakpoints.up('sm')]: {
    fontSize: '1.5rem',
    padding: '1.125rem 0 1rem',
  },
  [almaStyleArticleTheme.breakpoints.up('md')]: {
    fontSize: '1.75rem',
    lineHeight: '150%',
    padding: '1.25rem 0',
  },
};

almaStyleArticleTheme.typography.h5 = {
  fontSize: '1.25rem',
  lineHeight: '133%',
  fontWeight: 900,
  letterSpacing: '0.0625rem',
  padding: '1rem 0 0.5rem',
  [almaStyleArticleTheme.breakpoints.up('sm')]: {
    fontSize: '1.25rem',
    padding: '1.125rem 0 0.75rem',
  },
  [almaStyleArticleTheme.breakpoints.up('md')]: {
    fontSize: '1.5rem',
    lineHeight: '150%',
    padding: '1.25rem 0 0.75rem',
  },
};

almaStyleArticleTheme.typography.h6 = {
  fontSize: '1.125rem',
  lineHeight: '133%',
  fontWeight: 900,
  letterSpacing: '0.0625rem',
  padding: '1rem 0 0.5rem',
  [almaStyleArticleTheme.breakpoints.up('sm')]: {
    fontSize: '1.125rem',
    padding: '1.125rem 0 0.75rem',
  },
  [almaStyleArticleTheme.breakpoints.up('md')]: {
    fontSize: '1.25rem',
    lineHeight: '150%',
    padding: '1.25rem 0 0.75rem',
  },
};

almaStyleArticleTheme.typography.subtitle1 = {
  fontSize: '1.25rem',
  lineHeight: '175%',
  fontWeight: 400,
  padding: '0 0 1rem',
  [almaStyleArticleTheme.breakpoints.up('sm')]: {
    padding: '0 0 1.125rem',
    fontSize: '1.40625rem',
  },
  [almaStyleArticleTheme.breakpoints.up('md')]: {
    padding: '0 0 1.25rem',
    fontSize: '1.5625rem',
  },
};

const body1 = {
  fontSize: '1rem',
  lineHeight: '175%',
  fontWeight: 400,
  padding: '0 0 1rem',
  [almaStyleArticleTheme.breakpoints.up('sm')]: {
    fontSize: '1.125rem',
    padding: '0 0 1.125rem',
  },
  [almaStyleArticleTheme.breakpoints.up('md')]: {
    fontSize: '1.25rem',
    padding: '0 0 1.25rem',
  },
};
almaStyleArticleTheme.typography.body1 = body1;

almaStyleArticleTheme.typography.listItem = {
  ...body1,
  [almaStyleArticleTheme.breakpoints.up('sm')]: {
    fontSize: '1.125rem',
    padding: '0',
  },
  [almaStyleArticleTheme.breakpoints.up('md')]: {
    fontSize: '1.25rem',
    padding: '0',
  },
};

almaStyleArticleTheme.typography.body2 = {
  fontSize: '0.875rem',
  lineHeight: '166%',
  fontWeight: 400,
  padding: '0 0 0.875rem',
  [almaStyleArticleTheme.breakpoints.up('sm')]: {
    fontSize: '1rem',
    padding: '0 0 1rem',
  },
  [almaStyleArticleTheme.breakpoints.up('md')]: {
    fontSize: '1.125rem',
    padding: '0 0 1.25rem',
  },
};

/*TableCell uses body2 typography settings as a base, overriding most
properties, but the body2 mediaqueries are still applied, so we need to override
them here. Keeping font size the same though for consistency.*/
if (almaStyleArticleTheme.components?.MuiTableCell) {
  almaStyleArticleTheme.components.MuiTableCell = {
    styleOverrides: {
      root: {
        [almaStyleArticleTheme.breakpoints.up('sm')]: {
          padding: '1em',
          fontSize: '1rem',
        },
        [almaStyleArticleTheme.breakpoints.up('md')]: {
          padding: '1em',
          fontSize: '1.125rem',
        },
      },
    },
  };
}

export { almaStyleArticleTheme };
